/* RESPONSIBLE TEAM: team-automation-foundations */
import { buildValidations, validator } from 'ember-cp-validations';
import Step, { IsCustomerFacing } from 'embercom/models/operator/visual-builder/step';
import { attr } from '@ember-data/model';
import type Store from '@ember-data/store';
import { inject as service } from '@ember/service';
import { SUPPORTED_CHANNELS } from 'embercom/lib/operator/custom-bots/constants-2';
// eslint-disable-next-line no-restricted-imports
import containerLookup, { getApp } from 'embercom/lib/container-lookup';
import type FinAgentTypeService from 'embercom/services/fin-agent-type-service';
import type { FinAgentSelection } from 'embercom/services/fin-agent-type-service';
/* eslint-disable ember/no-computed-properties-in-native-classes */
import { not } from '@ember/object/computed';
import { computed } from '@ember/object';
import { resolutionBotBillingFeature } from 'embercom/components/operator/answer-bot/billing-helper';
import type ConnectionPoint from '../connection-point';
import type ContentImportService from 'embercom/services/content-import-service';
import { type AiContentLibrarySummary } from 'embercom/services/content-import-service';
import type FinStandaloneService from 'embercom/services/fin-standalone-service';
import { AiAnswersState } from 'embercom/lib/operator/fin/types';
import { tracked } from '@glimmer/tracking';
import { TargetContext } from 'embercom/objects/operator/configuration/custom-bot';
import { type Block } from '@intercom/interblocks.ts';
import { fragment, fragmentArray } from 'ember-data-model-fragments/attributes';
import type HardResolutionWorkflow from 'embercom/models/operator/fin/hard-resolution-workflow';
import type InactivityWorkflow from 'embercom/models/operator/fin/inactivity-workflow';
import generateUUID from 'embercom/lib/uuid-generator';
import type { FollowupOption } from 'embercom/lib/workflows/fin-workflow-preview';
import type HandoffToExternalTicketOnResolutionData from 'embercom/models/operator/fin/handoff-to-external-ticket-on-resolution-data';
import { AGENT_OPTIONS } from 'embercom/lib/operator/resolution-bot/constants';

const MIN_EMAIL_FOLLOW_UP_DURATION = 3600; // 1 hour
const MAX_EMAIL_FOLLOW_UP_DURATION = 30 * 24 * 60 * 60; // 30 days in seconds

function agentTypeToSelectedAgent(agentType: FinAgentSelection): string {
  switch (agentType) {
    case 'ecommerce':
      return AGENT_OPTIONS.FIN_ECOMMERCE;
    case 'sales':
      return AGENT_OPTIONS.FIN_SDR;
    case 'success':
      return AGENT_OPTIONS.FIN_SUCCESS;
    default:
      return AGENT_OPTIONS.FIN;
  }
}

interface AppStandaloneCapabilities {
  canUseStandalone: boolean;
  canUseFeature: (flag: string) => boolean;
}

// Standalone workspaces cannot use eCommerce, and can only use Sales when
// ca-fin-sales-standalone is enabled. Return a fallback agent type so callers
// don't seed or display a hidden, unswitchable agent type.
export function normalizeSelectedAgentForStandalone(
  selectedAgent: string,
  app: AppStandaloneCapabilities,
): string {
  if (!app.canUseStandalone) {
    return selectedAgent;
  }
  if (selectedAgent === AGENT_OPTIONS.FIN_ECOMMERCE) {
    return AGENT_OPTIONS.FIN;
  }
  if (selectedAgent === AGENT_OPTIONS.FIN_SDR && !app.canUseFeature('ca-fin-sales-standalone')) {
    return AGENT_OPTIONS.FIN;
  }
  return selectedAgent;
}

const Validations = buildValidations({
  outwardConnectionPoints: [validator('has-many')],
  inactivityTimer: [
    validator('number', {
      allowNone: true,
      integer: true,
      gte: 60,
      lte: 180 * 24 * 60 * 60, // 180 days in seconds
      messageKey: 'operator.workflows.visual-builder.validations.fin.timer',
    }),
  ],
  emailFollowUpTimer: [
    validator('number', {
      allowNone: true,
      integer: true,
      gte: MIN_EMAIL_FOLLOW_UP_DURATION,
      lte: MAX_EMAIL_FOLLOW_UP_DURATION,
      messageKey: 'operator.workflows.visual-builder.validations.fin.follow-up-timer',
    }),
  ],
  hasAiContentReadyForFin: {
    descriptionKey: 'ai-agent.profiles.validation-errors.ai-content-not-ready',
    validators: [
      validator('fin-has-ai-content-ready', {
        disabled: not('model.useAiAnswers'),
      }),
    ],
    dependentKeys: [
      'model.useAiAnswers',
      'model.app.isStandaloneApp',
      'model.app.usesResolutionsWithCustomAnswers',
      'model.contentImportService.aiContentLibrarySummary',
      'model.finStandaloneService.isLoadingAiContentLibrarySummary',
      'model.customAnswerCount',
      'model.selectedAgentValue',
      'model.isFinSdr',
    ],
  },
  hasSdrConfigForFin: {
    descriptionKey: 'ai-agent.profiles.validation-errors.sdr-config-not-ready',
    validators: [
      validator('fin-has-sdr-config', {
        disabled: not('model.isFinSdr'),
      }),
    ],
    dependentKeys: [
      'model.selectedAgent',
      'model.selectedAgentValue',
      'model.isFinSdr',
      'model.hasSdrConfig',
    ],
  },
  hasSalesAgentContentForFin: {
    descriptionKey: 'ai-agent.profiles.validation-errors.sdr-content-not-ready',
    validators: [validator('fin-has-sales-agent-content')],
    dependentKeys: [
      'model.selectedAgent',
      'model.selectedAgentValue',
      'model.isFinSdr',
      'model.hasSalesAgentContent',
    ],
  },
  autoCloseFollowUpMessages: {
    validators: [validator('has-many')],
  },
  hardResolutionWorkflow: {
    validators: [validator('belongs-to')],
  },
  inactivityWorkflow: {
    validators: [validator('belongs-to')],
  },
  emailSetupValid: [
    validator('ai-agent-email-setup-valid', {
      disabled: not('model.validateEmailSetup'),
      dependentKeys: ['app.dkim_settings.[]', 'app.customEmailAddresses.[]'],
    }),
  ],
});

export default class Fin extends Step.extend(Validations) {
  @attr('boolean') declare useCustomAnswers: boolean;
  @attr('string') declare botMode: 'looping' | 'one_time' | 'bot_only';
  @attr('boolean') declare useFinInactivitySettings: boolean;
  @attr('number') declare inactivityTimer: number;
  @attr('number') declare emailFollowUpTimer: number | null;
  @attr('boolean') declare autoCloseWithAnswer: boolean;
  @attr('boolean') declare autoCloseNoAnswer: boolean;
  @attr('boolean') declare autoCloseNoQuestion: boolean;
  @attr('boolean') declare abandonedLeadFollowupEnabled: boolean;
  @attr('boolean') declare escalationDisabled: boolean;
  @attr('boolean') declare csatEnabledForHardResolution: boolean;
  @attr('boolean') declare csatEnabledForSoftResolution: boolean;
  @attr('string') declare csatCustomMessage: string;
  @attr('boolean') declare useCsatCustomMessage: boolean;
  @attr('number') declare csatBlockUpdateAfter?: number;
  @attr('boolean') declare preHandoverAnswerEnabled: boolean;
  @attr('boolean') declare confirmResolutionEnabled: boolean;
  @fragment('operator/fin/handoff-to-external-ticket-on-resolution-data')
  declare handoffToExternalTicketOnResolutionData: HandoffToExternalTicketOnResolutionData;
  @fragment('operator/fin/hard-resolution-workflow')
  declare hardResolutionWorkflow?: HardResolutionWorkflow | null;
  @fragment('operator/fin/inactivity-workflow')
  declare inactivityWorkflow?: InactivityWorkflow | null;
  @fragmentArray('operator/fin/auto-close-message') declare autoCloseFollowUpMessages: [Block[]];
  @attr('string') declare externalStepId: string;
  @attr('string') declare followUpType: FollowupOption;
  @attr('string', { defaultValue: () => 'follow_up' }) declare emailFollowUpType: string; // "follow_up" | "no_follow_up"
  @attr('boolean', { defaultValue: () => false }) declare finSilentWhenCcRecipient: boolean;
  @attr('boolean', { defaultValue: () => false }) declare finReplyToSenderOnly: boolean;
  @attr('string', { defaultValue: () => AGENT_OPTIONS.FIN }) declare selectedAgent: string;
  @attr('boolean') declare hitlEnabled: boolean;
  @attr('number') declare hitlConfidenceThreshold: number;
  @attr('number') declare hitlAssigneeId: number | null;
  @attr('number') declare hitlTimeoutSeconds: number;
  @fragment('predicates/predicate-group') declare hitlConditions: any;
  minInactivityDuration = 60;
  maxInactivityDuration = 180 * 24 * 60 * 60; // 180 days in seconds
  minEmailFollowUpDuration = MIN_EMAIL_FOLLOW_UP_DURATION;
  maxEmailFollowUpDuration = MAX_EMAIL_FOLLOW_UP_DURATION;

  get supportedChannels() {
    return SUPPORTED_CHANNELS.fin;
  }

  @service declare contentImportService: ContentImportService; // needed for validation
  @service declare finStandaloneService: FinStandaloneService; // needed for validation
  @service declare contentEditorService: $TSFixMe;

  @tracked aiContentLibrarySummary: AiContentLibrarySummary = {};
  // setting the initial value to -1 to pass validation while fetching answers
  @tracked customAnswerCount = -1;
  // null = loading, true = SDR config exists, false = no SDR config
  @tracked hasSdrConfig: boolean | null = null;
  // null = loading, true = sales agent content exists, false = no content
  @tracked hasSalesAgentContent: boolean | null = null;

  get app() {
    return this.appService.app;
  }

  @computed(
    'app.{dkim_settings.[],customEmailAddresses.[]}',
    'contentEditorService.activeObject.targetChannels.[]',
  )
  get validateEmailSetup() {
    return this.contentEditorService.activeObject?.targetChannels?.includes('email');
  }

  get preHandoverAnswerAllowed() {
    return this.useAiAnswers && this.botMode !== 'bot_only';
  }

  static get supportedContexts() {
    return [TargetContext.Conversation, TargetContext.CustomerTicket];
  }

  get isAnyCSATSettingEnabled() {
    return this.csatEnabledForHardResolution || this.csatEnabledForSoftResolution;
  }

  get selectedAgentValue() {
    return this.selectedAgent ?? AGENT_OPTIONS.FIN;
  }

  get isFinSdr() {
    return this.selectedAgentValue === AGENT_OPTIONS.FIN_SDR;
  }

  get isFinEcommerce() {
    return this.selectedAgentValue === AGENT_OPTIONS.FIN_ECOMMERCE;
  }

  get isFinSuccess() {
    return this.selectedAgentValue === AGENT_OPTIONS.FIN_SUCCESS;
  }

  get isFinProactive() {
    return this.selectedAgentValue === AGENT_OPTIONS.FIN_PROACTIVE;
  }

  static get isStepGroupEnding() {
    return true;
  }

  static get canBeCustomerFacing() {
    return IsCustomerFacing.Yes;
  }

  static get requiredBillingFeature() {
    let app = getApp();

    return resolutionBotBillingFeature(app);
  }

  static createNewStep(store: Store): Fin {
    let outwardConnectionPoints = [
      store.createRecord('operator/visual-builder/connection-point', { type: 'fin_triage' }),
    ] as ConnectionPoint[];

    let app = getApp();

    let finAgentTypeService = containerLookup('service:finAgentTypeService') as
      | FinAgentTypeService
      | undefined;
    let selectedAgent = normalizeSelectedAgentForStandalone(
      finAgentTypeService
        ? agentTypeToSelectedAgent(finAgentTypeService.currentAgentType)
        : AGENT_OPTIONS.FIN,
      app,
    );

    return store.createRecord('operator/visual-builder/step/fin', {
      type: 'operator/visual-builder/step/fin',
      outwardConnectionPoints,
      useAiAnswers: this.canUseAiAnswers(app) && !app.finBlockedInTestApp,
      useCustomAnswers: app.hasAnswerBot,
      botMode: 'looping',
      useFinInactivitySettings: true,
      inactivityTimer: 180,
      autoCloseWithAnswer: true,
      autoCloseNoAnswer: false,
      autoCloseNoQuestion: true,
      autoCloseFollowUpMessages: [],
      abandonedLeadFollowupEnabled: false,
      preHandoverAnswerEnabled: this.canUseAiAnswers(app) && !app.finBlockedInTestApp,
      externalStepId: generateUUID(),
      selectedAgent,
    });
  }

  static canUseAiAnswers(app: { aiAnswersState: AiAnswersState }) {
    return [
      AiAnswersState.active,
      AiAnswersState.trialNotStarted,
      AiAnswersState.trialStarted,
    ].includes(app.aiAnswersState);
  }
}
